import { Category, Launch, Schedule } from '@mui/icons-material';
import { Box, Button, Chip, Divider, Grid2, Paper, Typography } from '@mui/material';
import { isShibUser } from '@thro-helpers/users';
import { StudentJobOffer } from '@thro-types/jobmarket';

import { format } from 'date-fns';
import parse from 'html-react-parser';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useLocation, useParams } from 'react-router';

import {
  useStudentJobOfferingQuery,
  useStudentJobOfferingsMutation
} from 'api/hooks/jobmarket/studentOffers';
import { ConfirmationDialog } from 'app/components';
import { WatchlistAddButton } from 'app/components/WatchlistAddButton';
import { AlertActions } from 'app/containers/Dashboard/Alert/slice';
import { translations } from 'locales/i18n';
import { ACLHelper } from 'utils/ACLHelper';
import { useAuthContext } from 'utils/hooks/useAuthContext';
import usePageHeading from 'utils/hooks/usePageHeading';

import { ContactInfoDisplay } from '../../JobOfferDetail/components/ContactInfoDisplay';
import { StudentJobOfferDrawer } from '../StudenJobOfferDrawer/StudentJobOfferDrawer';
import { Menu } from '../menu';
import { StudentJobOfferDetailSkeleton } from './Skeleton';

export const StudentJobOfferDetailView = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const dispatch = useDispatch();
  const { user } = useAuthContext();
  const { id } = useParams<{ id: string }>();
  const { data: studentJobOfferData, isLoading } = useStudentJobOfferingQuery(id!);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const aclHelper = new ACLHelper(user);

  usePageHeading({
    heading: t(translations.pages.studentJobOffers.detailPage.title()),
    headingProps: {
      back: location.state?.back ? location.state.back : '/jobs/studentOffers'
    }
  });

  const { deletion } = useStudentJobOfferingsMutation();

  if (isLoading) {
    return <StudentJobOfferDetailSkeleton />;
  }
  const studentJobOffer = studentJobOfferData as StudentJobOffer;

  const canEdit = studentJobOffer.userId === user.id || aclHelper.isSuperUser();

  const handleDelete = async () => {
    try {
      if (!studentJobOffer.id) {
        return;
      }
      await deletion.mutateAsync(studentJobOffer.id);
      dispatch(AlertActions.success(t(translations.pages.studentJobOffers.alerts.deleteSuccess())));
    } catch {
      dispatch(AlertActions.error(t(translations.pages.studentJobOffers.alerts.deleteFailed())));
    } finally {
      setDeleteDialogOpen(false);
    }
  };

  const handleDeleteButton = () => {
    setDeleteDialogOpen(true);
  };

  const handleEdit = () => {
    setDrawerOpen(true);
  };

  const handleClose = () => {
    setDrawerOpen(false);
  };

  return (
    <>
      {isShibUser(user) && (
        <StudentJobOfferDrawer
          open={drawerOpen}
          handleClose={handleClose}
          studentOffering={studentJobOffer}
        />
      )}
      <ConfirmationDialog
        title={t(translations.common.reallyDelete())}
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDelete}
        isPending={deletion.isPending}
      >
        {t(translations.pages.studentJobOffers.dialogs.deleteConfirm())}
      </ConfirmationDialog>
      <Grid2 container>
        <Grid2
          //TODO wichtige css sachen rausziehen
          className="job-offer-detail__contact"
          component={Paper}
          square
          sx={{
            width: { xs: '100%', md: '25%' }
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 2 }}>
            {t(translations.pages.studentJobOffers.detailPage.personalData())}
          </Typography>
          <Grid2>
            <ContactInfoDisplay label={t(translations.common.name())}>
              {studentJobOffer.name}
            </ContactInfoDisplay>
            <ContactInfoDisplay label={t(translations.common.email())}>
              {studentJobOffer.email}
            </ContactInfoDisplay>
            {!!studentJobOffer.phone && (
              <ContactInfoDisplay label={t(translations.common.phone())}>
                {studentJobOffer.phone}
              </ContactInfoDisplay>
            )}
          </Grid2>
          <Divider />
          <Grid2
            container
            alignItems="flex-start"
            sx={{ mt: 2 }}
            gap={2}
            direction="column"
            spacing={1}
          >
            <Grid2 container direction="column" spacing={1}>
              <Grid2 container alignItems="center" spacing={0.5}>
                <Category className="job-offer-detail__job__category__icon" />
                <Typography variant="body1">{studentJobOffer?.jobType?.name}</Typography>
              </Grid2>
              <Grid2 container alignItems="center" spacing={0.5}>
                <Schedule sx={{ color: '#37b200' }} />
                <Typography variant="body1">
                  {t(translations.pages.jobOffers.start()) + ': '}
                  {format(new Date(studentJobOffer.startDate), 'dd.MM.yyyy')}
                </Typography>
              </Grid2>
            </Grid2>
            <Grid2 container justifyContent={'space-between'}>
              <Button
                sx={{}}
                size={'small'}
                startIcon={<Launch />}
                variant="contained"
                href={`mailto:${studentJobOffer.email}?subject=Antwort: Stellengesuch ${studentJobOffer.title}`}
              >
                {t(translations.pages.studentJobOffers.detailPage.contactNow())}
              </Button>
              <WatchlistAddButton
                categoryName="STUDENT_OFFER"
                description={studentJobOffer.description}
                title={studentJobOffer.title}
                date={new Date()}
                withoutDateFilter
              />
            </Grid2>
          </Grid2>
        </Grid2>
        <Grid2
          component={Paper}
          square
          sx={{
            width: { xs: '100%', md: '75%' },
            padding: '1em'
          }}
          //TODO wichtige css sachen rausziehen
          className="job-offer-detail__job"
        >
          <Grid2
            container
            sx={{
              flexDirection: 'column'
            }}
            gap={2}
          >
            <Grid2 container alignItems={'flex-end'} justifyContent={'space-between'}>
              <Chip
                key={studentJobOffer?.course?.id}
                className="joboffer__body__courses__course"
                label={studentJobOffer?.course?.shorthand}
                variant="outlined"
                color="primary"
                size="small"
              />
              {canEdit && (
                <Grid2 justifyContent={'flex-end'} alignItems={'flex-end'}>
                  <Menu handleEdit={handleEdit} handleDelete={handleDeleteButton} />
                </Grid2>
              )}
            </Grid2>
            <Grid2 container gap={2} direction="column">
              <Typography
                variant="h5"
                sx={{ fontWeight: 'bold', whiteSpace: 'pre-line', wordBreak: 'break-word' }}
              >
                {studentJobOffer.title}
              </Typography>

              <div className="job-offer-detail__job__content__description">
                <Box sx={{ overflowY: 'auto', minHeight: '8em', wordBreak: 'break-word' }}>
                  <Typography variant="body1">{parse(studentJobOffer.description)}</Typography>
                </Box>
              </div>
            </Grid2>
          </Grid2>
        </Grid2>
      </Grid2>
    </>
  );
};
